// Notes, pins and deadline reminders with Undo (invariant 8). All local: nothing here reaches Gmail,
// and a note never reaches Claude.
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { MemoryView } from "@shared/api";
import { isApiError } from "@web/lib/api";
import { acceptCard, invalidateBoards } from "@web/lib/feedQueries";
import { invalidateMemory, rememberApi, rememberKeys } from "@web/lib/rememberApi";
import { tasksApi } from "@web/lib/tasksApi";
import { acceptTask, invalidateTasks } from "@web/lib/tasksQueries";
import { registerUndo } from "@web/lib/undoRegistry";
import { noteMessage, reminderMessage } from "./wording";

function acceptMemory(queryClient: QueryClient, view: MemoryView, cardId?: string): void {
  if (cardId) queryClient.setQueryData(rememberKeys.card(cardId), view);
  invalidateMemory(queryClient);
}

/** Saves (or, with empty text, removes) the note on a thread; Undo puts the old text back. */
export async function saveThreadNote(
  queryClient: QueryClient,
  threadKey: string,
  text: string,
  previous: string | null,
): Promise<boolean> {
  const next = text.trim();
  if (next === (previous ?? "")) return true;
  try {
    acceptMemory(queryClient, await rememberApi.saveNote(threadKey, next));
  } catch (error) {
    toast(
      isApiError(error, "invalid_request") && error.serverMessage
        ? `Couldn't save the note: ${error.serverMessage}.`
        : "Couldn't save the note just now. Your text is still here.",
    );
    return false;
  }
  registerUndo({
    message: noteMessage(previous, next),
    undo: async () =>
      acceptMemory(queryClient, await rememberApi.saveNote(threadKey, previous ?? "")),
    doneMessage: previous ? "The note is back as it was." : "Note removed.",
  });
  return true;
}

/** Pins or unpins a card; Undo flips it back. */
export async function setCardPin(
  queryClient: QueryClient,
  cardId: string,
  pinned: boolean,
): Promise<void> {
  try {
    acceptMemory(queryClient, await rememberApi.pin(cardId, pinned), cardId);
    invalidateBoards(queryClient);
  } catch {
    toast(pinned ? "Couldn't pin this card just now." : "Couldn't unpin this card just now.");
    return;
  }
  registerUndo({
    message: pinned ? "Pinned. It stays in view across rounds." : "Unpinned.",
    undo: async () => {
      acceptMemory(queryClient, await rememberApi.pin(cardId, !pinned), cardId);
      invalidateBoards(queryClient);
    },
    doneMessage: pinned ? "Unpinned." : "Pinned again.",
  });
}

/** "Remind me the day before": a task with its reminder; Undo drops the task. */
export async function remindBeforeDeadline(
  queryClient: QueryClient,
  cardId: string,
  when: { words: string | null; remindAt: string | null },
): Promise<void> {
  let taskId: string;
  try {
    const { card, task } = await rememberApi.deadlineReminder(cardId);
    acceptCard(queryClient, card);
    acceptTask(queryClient, task);
    invalidateBoards(queryClient);
    invalidateTasks(queryClient);
    invalidateMemory(queryClient);
    taskId = task.id;
  } catch (error) {
    toast(
      isApiError(error, "invalid_request") && error.serverMessage
        ? `${error.serverMessage}.`
        : "Couldn't make the reminder just now. Nothing changed.",
    );
    return;
  }
  registerUndo({
    message: reminderMessage(when.words, when.remindAt),
    undo: async () => {
      acceptTask(queryClient, await tasksApi.setStatus(taskId, "drop"));
      invalidateMemory(queryClient);
    },
    doneMessage: "Reminder dropped.",
  });
}
